import { useState, useEffect, useCallback, useMemo } from 'react';
import { EventService } from '../services/eventService.js';
import { authHelpers } from '../lib/supabase.js';

// Custom hook for managing events with Supabase integration
export const useEvents = (initialFilters = {}) => {
  const [events, setEvents] = useState([]);
  const [featuredEvents, setFeaturedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);

  // Memoize initialFilters to prevent reference changes causing infinite re-renders
  const memoizedInitialFilters = useMemo(() => initialFilters, [
    initialFilters?.status,
    initialFilters?.category,
    initialFilters?.location,
    initialFilters?.search,
    initialFilters?.dateFrom,
    initialFilters?.dateTo,
    initialFilters?.minPrice,
    initialFilters?.maxPrice,
    initialFilters?.sortBy,
    initialFilters?.sortOrder,
    initialFilters?.page,
    initialFilters?.pageSize
  ]);

  // Get current user on mount
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { user, profile } = await authHelpers?.getCurrentUserWithProfile();
        setCurrentUser(profile);
      } catch (err) {
        console.error('Error getting current user:', err);
      }
    };
    getCurrentUser();
  }, []);

  // Fetch all events with filters
  const fetchEvents = useCallback(async (filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Use passed filters or fallback to memoized initial filters
      const filtersToUse = Object.keys(filters)?.length > 0 ? filters : memoizedInitialFilters;
      const { data, error: fetchError, count } = await EventService?.getAllEvents(filtersToUse);

      if (fetchError) {
        setError(fetchError);
        setEvents([]);
        setTotalCount(0);
      } else {
        setEvents(data || []);
        setTotalCount(count || 0);
      }
    } catch (err) {
      console.error('Error in fetchEvents:', err);
      setError(err?.message || 'Failed to fetch events');
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [memoizedInitialFilters]);

  // Fetch featured events
  const fetchFeaturedEvents = useCallback(async (limit = 6) => {
    try {
      const { data, error: fetchError } = await EventService?.getFeaturedEvents(limit);

      if (fetchError) {
        console.error('Error fetching featured events:', fetchError);
        setFeaturedEvents([]);
      } else {
        setFeaturedEvents(data || []);
      }
    } catch (err) {
      console.error('Error in fetchFeaturedEvents:', err);
      setFeaturedEvents([]);
    }
  }, []);

  // Search events
  const searchEvents = useCallback(async (searchTerm, filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: searchError } = await EventService?.searchEvents(searchTerm, filters);

      if (searchError) {
        setError(searchError);
        setEvents([]);
      } else {
        setEvents(data || []);
        setTotalCount(data?.length || 0);
      }
    } catch (err) {
      console.error('Error in searchEvents:', err);
      setError(err?.message || 'Failed to search events');
      setEvents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get event by ID
  const getEventById = useCallback(async (eventId) => {
    try {
      const { data, error: fetchError } = await EventService?.getEventById(eventId);

      if (fetchError) {
        console.error('Error fetching event by ID:', fetchError);
        return { data: null, error: fetchError };
      }

      return { data, error: null };
    } catch (err) {
      console.error('Error in getEventById:', err);
      return { data: null, error: err?.message || 'Failed to fetch event' };
    }
  }, []);

  // Create event
  const createEvent = useCallback(async (eventData) => {
    try {
      if (!currentUser?.id) {
        throw new Error('User authentication required');
      }

      const { data, error: createError } = await EventService?.createEvent(eventData, currentUser);

      if (createError) {
        console.error('Error creating event:', createError);
        return { data: null, error: createError };
      }

      // Refresh events list with current filters
      await fetchEvents(memoizedInitialFilters);

      return { data, error: null };
    } catch (err) {
      console.error('Error in createEvent:', err);
      return { data: null, error: err?.message || 'Failed to create event' };
    }
  }, [currentUser, fetchEvents, memoizedInitialFilters]);

  // Update event
  const updateEvent = useCallback(async (eventId, eventData) => {
    try {
      if (!currentUser?.id) {
        throw new Error('User authentication required');
      }

      const { data, error: updateError } = await EventService?.updateEvent(eventId, eventData, currentUser);

      if (updateError) {
        console.error('Error updating event:', updateError);
        return { data: null, error: updateError };
      }

      // Refresh events list with current filters
      await fetchEvents(memoizedInitialFilters);

      return { data, error: null };
    } catch (err) {
      console.error('Error in updateEvent:', err);
      return { data: null, error: err?.message || 'Failed to update event' };
    }
  }, [currentUser, fetchEvents, memoizedInitialFilters]);

  // Delete event
  const deleteEvent = useCallback(async (eventId) => {
    try {
      if (!currentUser?.id) {
        throw new Error('User authentication required');
      }

      const { success, error: deleteError } = await EventService?.deleteEvent(eventId, currentUser);

      if (deleteError) {
        console.error('Error deleting event:', deleteError);
        return { success: false, error: deleteError };
      }

      // Refresh events list with current filters
      await fetchEvents(memoizedInitialFilters);

      return { success, error: null };
    } catch (err) {
      console.error('Error in deleteEvent:', err);
      return { success: false, error: err?.message || 'Failed to delete event' };
    }
  }, [currentUser, fetchEvents, memoizedInitialFilters]);

  // Get events by current user (if they are a host)
  const getUserEvents = useCallback(async (filters = {}) => {
    try {
      if (!currentUser?.id) {
        return { data: [], error: 'User authentication required' };
      }

      const { data, error: fetchError } = await EventService?.getEventsByHost(currentUser?.id, filters);

      if (fetchError) {
        console.error('Error fetching user events:', fetchError);
        return { data: [], error: fetchError };
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error('Error in getUserEvents:', err);
      return { data: [], error: err?.message || 'Failed to fetch user events' };
    }
  }, [currentUser]);

  // Get event categories with counts
  const getEventCategories = useCallback(async () => {
    try {
      const { data, error: fetchError } = await EventService?.getEventCategories();

      if (fetchError) {
        console.error('Error fetching event categories:', fetchError);
        return { data: {}, error: fetchError };
      }

      return { data: data || {}, error: null };
    } catch (err) {
      console.error('Error in getEventCategories:', err);
      return { data: {}, error: err?.message || 'Failed to fetch event categories' };
    }
  }, []);

  // Subscribe to real-time events
  const subscribeToEvents = useCallback((callback, filters = {}) => {
    return EventService?.subscribeToEvents((payload) => {
      // Handle real-time updates
      if (payload?.eventType === 'INSERT') {
        setEvents(prev => [payload?.new, ...prev]);
      } else if (payload?.eventType === 'UPDATE') {
        setEvents(prev => 
          prev?.map(event => 
            event?.id === payload?.new?.id ? { ...event, ...payload?.new } : event
          )
        );
      } else if (payload?.eventType === 'DELETE') {
        setEvents(prev => prev?.filter(event => event?.id !== payload?.old?.id));
      }

      callback?.(payload);
    }, filters);
  }, []);

  // Refresh data
  const refreshEvents = useCallback(() => {
    return fetchEvents(memoizedInitialFilters);
  }, [fetchEvents, memoizedInitialFilters]);

  const refreshFeaturedEvents = useCallback(() => {
    return fetchFeaturedEvents();
  }, [fetchFeaturedEvents]);

  // Helper functions for permissions
  const canCreateEvent = currentUser?.role === 'host' || currentUser?.role === 'admin';
  const canManageEvent = (eventHostId) => {
    return currentUser?.role === 'admin' || (currentUser?.role === 'host' && currentUser?.id === eventHostId);
  };

  return {
    // State
    events,
    featuredEvents,
    loading,
    error,
    totalCount,
    currentUser,

    // Actions
    fetchEvents,
    fetchFeaturedEvents,
    searchEvents,
    getEventById,
    createEvent,
    updateEvent,
    deleteEvent,
    getUserEvents,
    getEventCategories,
    subscribeToEvents,
    refreshEvents,
    refreshFeaturedEvents,

    // Helpers
    canCreateEvent,
    canManageEvent
  };
};

export default useEvents;